// src/components/Header.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Navbar, Nav, NavDropdown, Modal, Button, Badge } from 'react-bootstrap'; // ⬅️ added Modal, Button
import { useAuth } from '../contexts/AuthContext';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { FlagFill } from 'react-bootstrap-icons';
import { useFirestore } from '../contexts/FirestoreContext';
import './Header.css';

function Header() {
  const { currentUser, logout, resetPassword } = useAuth(); // ⬅️ use resetPassword
  const { onSnapshotQueryData } = useFirestore();
  const navigate = useNavigate();
  const location = useLocation();

  const [role, setRole] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showCodesDropdown, setShowCodesDropdown] = useState(false);
  const [pendingRequestCount, setPendingRequestCount] = useState(0);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const pendingModalShownRef = useRef(false);

  // ⬇️ account modal + feedback state
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const dropdownRef = useRef(null);
  const codesDropdownRef = useRef(null);

  useEffect(() => {
    if (currentUser) {
      const auth = getAuth();
      onAuthStateChanged(auth, async (user) => {
        if (user) {
          const idTokenResult = await user.getIdTokenResult();
          setRole(idTokenResult.claims.role);
        }
      });
    }
  }, [currentUser]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        codesDropdownRef.current &&
        !codesDropdownRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
        setShowCodesDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (role !== 'Admin') {
      setPendingRequestCount(0);
      return;
    }

    const unsubscribe = onSnapshotQueryData(
      'accountRequests',
      'status',
      '==',
      'pending',
      (data) => setPendingRequestCount(data.length)
    );

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [role, onSnapshotQueryData]);

  useEffect(() => {
    pendingModalShownRef.current = false;
  }, [currentUser?.uid]);

  useEffect(() => {
    if (role === 'Admin' && pendingRequestCount > 0 && !pendingModalShownRef.current) {
      setShowPendingModal(true);
      pendingModalShownRef.current = true;
    } else if (role !== 'Admin' || pendingRequestCount === 0) {
      setShowPendingModal(false);
    }
  }, [role, pendingRequestCount]);

  async function handleLogout() {
    try {
      await logout();
      setShowAccountModal(false);
      navigate('/');
    } catch {
      console.error('Failed to log out');
    }
  }

  async function handleResetPassword() {
    if (!currentUser?.email) return;
    setFeedback(null);
    try {
      await resetPassword(currentUser.email);
      setFeedback(`Password reset email sent to ${currentUser.email}.`);
    } catch (e) {
      console.error(e);
      setFeedback(`Could not send reset email: ${e.message || e}`);
    }
  }

  const handleAdminClick = () => {
    setShowDropdown(!showDropdown);
    setShowCodesDropdown(false);
  };

  const handleCodesClick = (e) => {
    e.stopPropagation();
    setShowCodesDropdown(!showCodesDropdown);
  };

  const handleAdminSelect = (section) => {
    navigate(`/admin?section=${section}`);
    setShowDropdown(false);
    setShowCodesDropdown(false);
  };

  const handlePendingRequestClick = () => {
    navigate('/admin?section=users');
    setShowDropdown(false);
    setShowCodesDropdown(false);
    setShowPendingModal(false);
    pendingModalShownRef.current = true;
  };

  const handlePendingModalClose = () => {
    setShowPendingModal(false);
    pendingModalShownRef.current = true;
  };

  const isAdminRoute = () => location.pathname.startsWith('/admin');

  const getSectionName = () => {
    const params = new URLSearchParams(location.search);
    const section = params.get('section');
    if (!section) return '';
    if (section === 'salesids') return 'Sales IDs';
    if (section === 'reportingTypes') return 'Reporting Types';
    if (section === 'colorCodes') return 'Vehicle Colors';
    if (section === 'eventCodes') return 'Event Codes';
    if (section === 'modelCodes') return 'Model Codes';
    return section.charAt(0).toUpperCase() + section.slice(1);
  };

  // ⬇️ Name to show (displayName > custom name > email prefix)
  const displayName =
    currentUser?.displayName ||
    currentUser?.name ||
    (currentUser?.email ? currentUser.email.split('@')[0] : '');

  return (
    <Navbar bg="light" expand="lg" className="px-3">
      <Navbar.Text>
        <img
          src={require('../assets/logo-icon.png')}
          alt="Logo Icon"
          style={{ height: '30px', width: '30px', marginRight: '5px' }}
        />
      </Navbar.Text>
      <Navbar.Brand as={Link} to="/" className="me-3">
        <img
          src={require('../assets/logo.png')}
          alt="Logo"
          style={{ height: '30px', width: 'auto' }}
        />
      </Navbar.Brand>
      <Navbar.Toggle aria-controls="basic-navbar-nav" />
      <Navbar.Collapse id="basic-navbar-nav">
        <Nav className="me-auto">
          {currentUser && (
            <>
              {(role === 'Admin' || role === 'Sales Manager' || role === 'Sales Rep') && (
                <Nav.Link
                  as={Link}
                  to="/salesorders"
                  className={location.pathname === '/salesorders' ? 'active' : ''}
                >
                  Sales Orders
                </Nav.Link>
              )}

              {(role === 'Admin' || role === 'Sales Manager' || role === 'PDI' || role === 'Sales Rep') && (
                <Nav.Link
                  as={Link}
                  to="/vehicles"
                  className={location.pathname === '/vehicles' ? 'active' : ''}
                >
                  Vehicles
                </Nav.Link>
              )}

              {(role === 'Admin' || role === 'Sales Manager' || role === 'BDC' || role === 'Sales Rep') && (
                <NavDropdown
                  title="Admin"
                  id="admin-dropdown"
                  show={showDropdown}
                  onClick={handleAdminClick}
                  ref={dropdownRef}
                  className={isAdminRoute() ? 'admin-active' : ''}
                >
                  {role !== 'BDC' && (
                    <>
                      <NavDropdown.Item onClick={() => handleAdminSelect('customers')}>Customers</NavDropdown.Item>
                      <NavDropdown.Item onClick={() => handleAdminSelect('upfitters')}>Upfitters</NavDropdown.Item>
                    </>
                  )}

                  {/* NOTE: You had 'Sales Reps' elsewhere—ensure your role strings match exactly. */}
                  {role !== 'Sales Rep' && role !== 'BDC' && (
                    <>
                      <NavDropdown.Item onClick={() => handleAdminSelect('contacts')}>Contacts</NavDropdown.Item>
                      <NavDropdown.Item onClick={() => handleAdminSelect('users')}>Users</NavDropdown.Item>
                      <NavDropdown.Item onClick={() => handleAdminSelect('salesids')}>Sales IDs</NavDropdown.Item>
                      <NavDropdown.Item onClick={() => handleAdminSelect('reportingTypes')}>Reporting Types</NavDropdown.Item>
                    </>
                  )}

                  {role === 'BDC' && (
                    <NavDropdown.Item onClick={() => handleAdminSelect('users')}>Users</NavDropdown.Item>
                  )}

                  {role !== 'Sales Manager' && role !== 'BDC' && (
                    <NavDropdown
                      title="Codes"
                      id="codes-dropdown"
                      show={showCodesDropdown}
                      onClick={handleCodesClick}
                      className="dropend"
                      ref={codesDropdownRef}
                    >
                      <NavDropdown.Item onClick={() => handleAdminSelect('colorCodes')}>Vehicle Colors</NavDropdown.Item>
                      <NavDropdown.Item onClick={() => handleAdminSelect('eventCodes')}>Event Codes</NavDropdown.Item>
                      <NavDropdown.Item onClick={() => handleAdminSelect('modelCodes')}>Model Codes</NavDropdown.Item>
                    </NavDropdown>
                  )}
                </NavDropdown>
              )}

              {(role === 'Admin' || role === 'Sales Manager' || role === 'Sales Rep') && (
                <Nav.Link
                  as={Link}
                  to="/reports"
                  className={location.pathname === '/reports' ? 'active' : ''}
                >
                  Reports
                </Nav.Link>
              )}

              {role === 'Customer' && (
                <>
                  <Nav.Link
                    as={Link}
                    to="/customer"
                    className={location.pathname === '/customer' ? 'active' : ''}
                  >
                    Customer Portal
                  </Nav.Link>
                  <Nav.Link
                    as={Link}
                    to="/customerreports"
                    className={location.pathname === '/customerreports' ? 'active' : ''}
                  >
                    Customer Reports
                  </Nav.Link>
                </>
              )}

              {role === 'Upfitter' && (
                <>
                  <Nav.Link
                    as={Link}
                    to="/upfitter"
                    className={location.pathname === '/upfitter' ? 'active' : ''}
                  >
                    Upfitter Portal
                  </Nav.Link>
                  <Nav.Link
                    as={Link}
                    to="/upfitterreports"
                    className={location.pathname === '/upfitterreports' ? 'active' : ''}
                  >
                    Upfitter Reports
                  </Nav.Link>
                </>
              )}

            </>
          )}
        </Nav>
      </Navbar.Collapse>

      {isAdminRoute() && (
        <div className="section-title">
          <h5 className="mb-0">{getSectionName()}</h5>
        </div>
      )}

      {role === 'Admin' && pendingRequestCount > 0 && (
        <Button
          variant="link"
          className="text-danger position-relative me-2 fw-semibold"
          onClick={handlePendingRequestClick}
        >
          <FlagFill size={20} className="me-1" />
          Pending Requests
          <Badge bg="danger" pill className="ms-2">
            {pendingRequestCount}
          </Badge>
        </Button>
      )}

      {currentUser && (
        <Navbar.Text className="ms-3">
          <img
            src={require('../assets/fleet-icons.png')}
            alt="Fleet Icons"
            style={{ height: '30px', width: '345px', marginRight: '10px' }}
          />
          {/* ⬇️ clickable name to open the modal */}
          Signed in as:{' '}
          <button
            type="button"
            className="btn btn-link p-0 align-baseline"
            onClick={() => setShowAccountModal(true)}
            style={{ textDecoration: 'underline' }}
          >
            {displayName}
          </button>
        </Navbar.Text>
      )}

      <Modal show={showPendingModal} onHide={handlePendingModalClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Pending Account Requests</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          There {pendingRequestCount === 1 ? 'is' : 'are'} currently{' '}
          <strong>{pendingRequestCount}</strong> pending account request
          {pendingRequestCount === 1 ? '' : 's'} awaiting review.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handlePendingModalClose}>
            Close
          </Button>
          <Button variant="primary" onClick={handlePendingRequestClick}>
            View Requests
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ⬇️ Account Modal */}
      <Modal
        show={showAccountModal}
        onHide={() => setShowAccountModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Account</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">Hi <strong>{displayName}</strong>!</p>
          <p className="mb-0">
            You’re signed in with <strong>{currentUser?.email}</strong>.
          </p>
          {feedback && (
            <div className="alert alert-info mt-3 mb-0" role="alert">
              {feedback}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleResetPassword}>
            Reset Password
          </Button>
          <Button variant="danger" onClick={handleLogout}>
            Log Out
          </Button>
        </Modal.Footer>
      </Modal>
    </Navbar>
  );
}

export default Header;
